/**
 * `wordCount` — locale-aware, Unicode-correct word counting for the per-pane
 * bottom bar's live `n` (design §3.3). Counts the words a READER sees:
 *   · read mode: strip the resource's `rendered_html` to text, then count;
 *   · edit mode: count the live editor's plain text.
 *
 * Tokenization uses **`Intl.Segmenter(lang, { granularity: "word" })`** counting
 * `isWordLike` segments — Unicode-correct + locale-aware across the unit's EN/DE/PL
 * languages, no dependency. Where `Intl.Segmenter` is absent (very old runtimes),
 * a `\p{L}\p{N}`-run fallback keeps the count sane. No lib reimplements this — it
 * is a browser primitive; the server-side parity path (if ever needed for an
 * export footer) is `unicodez` UAX-29 in Zig (design §3.3 note), never hand-rolled.
 */

/** Strip HTML markup to the visible text: drop `<script>/<style>` bodies, replace
 *  every tag with a space (so adjacent block text doesn't fuse), decode the few
 *  common entities, and collapse whitespace. Good enough for a reader-visible
 *  word count (not a sanitizer). */
export function stripHtml(html: string): string {
  return html
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

type SegmenterCtor = new (
  locale?: string,
  options?: { granularity?: "grapheme" | "word" | "sentence" },
) => {
  segment(input: string): Iterable<{ segment: string; isWordLike?: boolean }>;
};

function segmenterCtor(): SegmenterCtor | null {
  const intl = Intl as unknown as { Segmenter?: SegmenterCtor };
  return typeof intl.Segmenter === "function" ? intl.Segmenter : null;
}

/**
 * Count the words in `text` for `lang` (BCP-47, e.g. "en"/"de"/"pl"). Empty /
 * whitespace-only text is 0. Uses `Intl.Segmenter` word-like segments; falls back
 * to a `\p{L}\p{N}`-run split where the API is unavailable.
 */
export function countWords(text: string, lang?: string): number {
  const t = text.trim();
  if (t.length === 0) return 0;

  const Seg = segmenterCtor();
  if (Seg) {
    try {
      const seg = new Seg(lang || undefined, { granularity: "word" });
      let n = 0;
      for (const part of seg.segment(t)) if (part.isWordLike) n += 1;
      return n;
    } catch {
      /* fall through to the regex fallback */
    }
  }
  const matches = t.match(/[\p{L}\p{N}][\p{L}\p{N}'’\-]*/gu);
  return matches ? matches.length : 0;
}

/** Count the reader-visible words in an HTML fragment for `lang`. */
export function countWordsInHtml(html: string, lang?: string): number {
  return countWords(stripHtml(html), lang);
}

/** Estimated reading time in whole minutes at ~200 wpm (≥1 for any non-empty
 *  text). */
export function readingMinutes(words: number): number {
  if (words <= 0) return 0;
  return Math.max(1, Math.round(words / 200));
}

export interface WordCountStats {
  words: number;
  chars: number;
  readingMinutes: number;
}

/** The full detail-popup stat bundle for reader-visible `text`. `chars` counts
 *  code points (Unicode-correct, so a multi-byte grapheme is one char). */
export function wordCountStats(text: string, lang?: string): WordCountStats {
  const t = text.trim();
  const words = countWords(t, lang);
  return {
    words,
    chars: [...t].length,
    readingMinutes: readingMinutes(words),
  };
}

/** The detail-popup stat bundle computed from an HTML fragment. */
export function wordCountStatsFromHtml(html: string, lang?: string): WordCountStats {
  return wordCountStats(stripHtml(html), lang);
}
